// Exportação do histórico de um veículo (percurso do dia/período).
// GPX (para SIG/Google Earth), CSV (folha de cálculo) e PDF (resumo).
// Tudo client-side — corre no browser do operador (download normal).
import jsPDF from 'jspdf';

const safe = (s) => String(s || 'historico').replace(/[^a-z0-9]/gi, '_').toLowerCase();

function download(filename, mime, content) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const periodLabel = (data) => {
  const df = data?.day || '';
  const dt = data?.dayTo || df;
  return df === dt ? df : `${df}_${dt}`;
};

// ── GPX ──────────────────────────────────────────────────────────────
export function exportHistoryGPX(data, vehicle) {
  const pts = data?.points || [];
  const name = vehicle?.plate || vehicle?.name || 'Veículo';
  const seg = pts.map(p => {
    const t = p.timestamp ? `<time>${new Date(p.timestamp).toISOString()}</time>` : '';
    const spd = Number.isFinite(Number(p.speed)) ? `<speed>${(Number(p.speed) / 3.6).toFixed(2)}</speed>` : '';
    const crs = Number.isFinite(Number(p.heading)) ? `<course>${Number(p.heading)}</course>` : '';
    return `   <trkpt lat="${p.lat}" lon="${p.lng}">${t}${spd ? `<extensions>${spd}${crs}</extensions>` : ''}</trkpt>`;
  }).join('\n');
  const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="EcooGPS Planner" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata><name>${name}</name></metadata>
  <trk>
    <name>${name} · ${periodLabel(data)}</name>
    <trkseg>
${seg}
    </trkseg>
  </trk>
</gpx>`;
  download(`percurso_${safe(name)}_${periodLabel(data)}.gpx`, 'application/gpx+xml', gpx);
}

// ── CSV ──────────────────────────────────────────────────────────────
export function exportHistoryCSV(data, vehicle) {
  const pts = data?.points || [];
  const name = vehicle?.plate || vehicle?.name || 'Veículo';
  const rows = [['data_hora', 'latitude', 'longitude', 'velocidade_kmh', 'rumo_graus', 'ignicao']];
  for (const p of pts) {
    rows.push([
      p.timestamp ? new Date(p.timestamp).toLocaleString('pt-PT') : '',
      p.lat, p.lng,
      Math.round(Number(p.speed) || 0),
      Math.round(Number(p.heading) || 0),
      p.ignition === true ? 'on' : p.ignition === false ? 'off' : '',
    ]);
  }
  const csv = '﻿' + rows.map(r => r.map(c => {
    const s = String(c ?? '');
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(';')).join('\n');
  download(`percurso_${safe(name)}_${periodLabel(data)}.csv`, 'text/csv;charset=utf-8', csv);
}

// ── PDF (resumo do dia/período + tabela de viagens) ───────────────────
export function exportHistoryPDF(data, vehicle, driverName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210, margin = 14;
  const name = vehicle?.plate || vehicle?.name || 'Veículo';
  const trips = data?.trips || [];
  const fmtDur = (m) => { const h = Math.floor((m || 0) / 60); const mm = Math.round((m || 0) % 60); return h > 0 ? `${h}h${String(mm).padStart(2, '0')}` : `${mm}min`; };
  const clock = (s) => s ? new Date(s).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  // Cabeçalho
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, pageW, 24, 'F');
  doc.setFillColor(59, 130, 246); doc.rect(0, 24, pageW, 1.5, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text('HISTÓRICO DE PERCURSO', margin, 11);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`${name}${driverName ? ` · ${driverName}` : ''} · ${periodLabel(data)}`, margin, 18);

  let y = 34;
  // Estatísticas
  const stats = [
    ['Viagens', String(data?.totalTrips ?? trips.length)],
    ['Distância', `${data?.totalDistanceKm ?? '—'} km`],
    ['Vel. máx', `${data?.maxSpeed ?? '—'} km/h`],
  ];
  const bw = (pageW - margin * 2 - 12) / 3;
  stats.forEach((s, i) => {
    const x = margin + i * (bw + 6);
    doc.setFillColor(241, 245, 249); doc.roundedRect(x, y, bw, 16, 2, 2, 'F');
    doc.setTextColor(148, 163, 184); doc.setFontSize(7); doc.setFont('helvetica', 'bold');
    doc.text(s[0].toUpperCase(), x + 4, y + 6);
    doc.setTextColor(15, 23, 42); doc.setFontSize(12); doc.text(s[1], x + 4, y + 13);
  });
  y += 24;

  // Tabela de viagens
  doc.setTextColor(15, 23, 42); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text('Viagens', margin, y); y += 5;
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 116, 139);
  doc.text('#', margin, y); doc.text('Início', margin + 8, y); doc.text('Fim', margin + 52, y);
  doc.text('Dur.', margin + 96, y); doc.text('Dist.', margin + 116, y); doc.text('V.máx', margin + 136, y);
  doc.text('Origem → Destino', margin + 156, y);
  y += 2; doc.setDrawColor(226, 232, 240); doc.line(margin, y, pageW - margin, y); y += 4;

  doc.setFont('helvetica', 'normal'); doc.setTextColor(15, 23, 42);
  if (trips.length === 0) { doc.setTextColor(148, 163, 184); doc.text('Sem viagens no período.', margin, y); }
  trips.forEach((t, i) => {
    if (y > 280) { doc.addPage(); y = 20; }
    doc.setTextColor(15, 23, 42);
    doc.text(String(i + 1), margin, y);
    doc.text(clock(t.startTime), margin + 8, y);
    doc.text(clock(t.endTime), margin + 52, y);
    doc.text(fmtDur(t.durationMin), margin + 96, y);
    doc.text(`${t.distance ?? '—'} km`, margin + 116, y);
    doc.text(`${t.maxSpeed ?? '—'}`, margin + 136, y);
    const od = `${(t.startAddress || '—').slice(0, 22)} → ${(t.endAddress || '—').slice(0, 22)}`;
    doc.setTextColor(100, 116, 139); doc.text(od, margin + 156, y, { maxWidth: pageW - margin - (margin + 156) });
    y += 5;
  });

  // Rodapé
  doc.setFontSize(7); doc.setTextColor(148, 163, 184);
  doc.text(`EcooGPS Planner · gerado ${new Date().toLocaleString('pt-PT')}`, margin, 291);
  doc.save(`percurso_${safe(name)}_${periodLabel(data)}.pdf`);
}
